import StarIcon from "@mui/icons-material/Star";
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  Link,
  Typography,
  Breadcrumbs,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomPagination } from "../components/shared/CustomPagination";
import { useAuth } from "../context/AuthContext";
import { fetchAllProviders } from "../utils/APIHelper";
import { formatPatientCount, formatTime } from "../utils/service";
import { getContextAwareToken } from "../utils/storageHelper";

interface Doctor {
  id: number;
  name: string;
  specialty: string;
  experience: string;
  patients: string;
  rating: number;
  location: string;
  time: string;
  gender?: string;
  avatar: string;
}

export const DoctorList = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const token =
    getContextAwareToken(user?.token, user?.role as any) ||
    user?.token ||
    localStorage.getItem("token");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const formatSpecialization = (specs: string[] = [], visibleCount = 2) => {
    if (!Array.isArray(specs) || specs.length === 0) return "N/A";

    const visible = specs.slice(0, visibleCount);
    const remaining = specs.length - visibleCount;

    return remaining > 0
      ? `${visible.join(", ")} +${remaining} more`
      : visible.join(", ");
  };

  useEffect(() => {
    const loadDoctors = async () => {
      setLoading(true);
      const data = await fetchAllProviders(
        token as any,
        itemsPerPage,
        currentPage
      );

      setDoctors(data?.data || []);

      if (data?.pagination) {
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.total);
        setCurrentPage(data.pagination.page);
        setItemsPerPage(data.pagination.limit);
      }
      setLoading(false);
    };

    loadDoctors();
  }, [token, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <Box p={2} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading Doctors...</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {/* ✅ BREADCRUMB */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/user-layout")}
        >
          Home
        </Link>
        <Typography color="text.primary">Doctor List</Typography>
      </Breadcrumbs>

      <Typography variant="h6" mb={2} fontWeight={700}>
        Doctor List
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        gap="20px"
        justifyContent="flex-start"
      >
        {doctors &&
          doctors.length > 0 &&
          doctors.map((doctor: any) => (
            <Card
              key={doctor.provider_id}
              sx={{
                width: "calc(33.333% - 20px)",
                minWidth: "260px",
                boxShadow: 2,
                borderRadius: "12px",
                overflow: "hidden",
                padding: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={doctor.avatar}
                  alt={`${doctor.first_name} ${doctor.last_name}`}
                  sx={{ width: 56, height: 56 }}
                />
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {doctor.title} {doctor.first_name} {doctor.last_name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <StarIcon fontSize="small" htmlColor="#FBBF24" />
                    <Typography variant="body2" color="text.secondary">
                      {doctor.rating}/5
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" mt={2} mb={1} gap={2}>
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Experience
                  </Typography>
                  <Typography variant="body2">
                    {doctor.experience_years} Years
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Patient
                  </Typography>
                  <Typography variant="body2">
                    {formatPatientCount(
                      doctor?.statistics?.existingPatients || 0
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Specialty
                  </Typography>

                  <Tooltip title={doctor.specialization.join(", ")}>
                    <Typography variant="body2">
                      {formatSpecialization(doctor.specialization)}
                    </Typography>
                  </Tooltip>

                </Box>
              </Box>

              <Box display="flex" justifyContent="space-between" mt={1}>
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Time
                  </Typography>
                  <Typography variant="body2">
                    {formatTime(doctor.start_time)} to{" "}
                    {formatTime(doctor.end_time)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    Location
                  </Typography>
                  <Typography variant="body2">{doctor.location}</Typography>
                </Box>
              </Box>

              <Box mt={2}>
                <Link
                  component="button"
                  underline="hover"
                  fontWeight="bold"
                  color="primary"
                  onClick={() =>
                    navigate(`/user-layout/doctor-list/${doctor.provider_id}`, {
                      state: { doctor },
                    })
                  }
                >
                  View Profile
                </Link>
              </Box>
            </Card>
          ))}
      </Box>

      {/* Pagination */}
      {doctors.length > 0 && (
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </Box>
  );
};
